import _ from 'lodash'
import buildDatasetFieldSelectorMenu from 'scoreboard/components/bootslide/build-dataset-field-selector-menu'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import { defaultDatasetSourceLabel } from 'scoreboard/dashboards/widgets/default-data-source-label'
import ShapeModel from './shape-model'
import createWob from '../create-wob'
import defaultDatasetValueWidgetAggregationType from '../default-dataset-value-widget-aggregation-type'
import { DEFAULT_WIDGET_PROPERTIES, DEFAULT_WOB_ATTRIBUTES } from './create-shape-widget-menu'
import {
	SHAPE,
	SHAPE_TEXT_TYPE,
	FILL,
	SHAPE_SPECIFIC_COLOR
} from './shape-widget-properties'
import { DATA_SOURCE } from './shape-text-type'
import { NONE } from './shape-fill-type'
import { WHITE } from '../widget-background-type'

/**
 * Creates a menu item and submenu for adding a "dataset value" shape widget to
 * a dashboard. Note that this creates a "shape" widget, just with properties
 * that are appropriate for a "Dataset Value" widget.
 *
 * @param {Object} addWidgetMenu - the bootslide menu for adding widgets
 */
function createDatasetValueShapeMenu (addWidgetMenu) {
	return buildDatasetFieldSelectorMenu({
		menuLabel: 'dashboards.datasets.datasetValue',
		iconId: 'add-widget-dataset-single-value',
		bootslideMenu: addWidgetMenu,
		fieldSelectorTargetId: 'dataset-value-field',
		fieldSelectorOptions: {
			allowTotal: true,
			fieldListFilter
		},
		onFieldSelect: selected => {
			addValueWidget({ addWidgetMenu, selected })
		}
	})
}

/**
 * Predicate for filtering fields in DatasetFieldSelector. Only returns true
 * for dataset fields that are appropriate to use in a shape widget
 *
 * @param {DatasetField} f
 * @param {Dataset} datasetModel
 */
function fieldListFilter (f, datasetModel) {
	if (!isNumericDataType(f.dataType)) {
		// #19394 Any non-numeric fields can be used in dataset values (they can
		// use 'unique' aggregationType)
		return true
	}

	// If aggregation type is not 'none', it's definitely fine to use
	if (f.aggregationType !== 'none') {
		return true
	}

	// #18467 If aggregation type is 'none', the field can only be used
	// if its dataset's primary key's data type is 'rollup'
	// First, find the field's source dataset (could be a linked dataset)
	let sourceDataset = datasetModel.getDatasetForField(f)

	// Now make sure the PK isn't 'rollup'
	let sourceDatasetPK = _.find(sourceDataset.fields, { id: sourceDataset.primaryKeyFieldId })
	// #18600 not all datasets have a PK defined, and if there
	// isn't a rollup PK we can't display the field
	return isGeographicOrRollup(_.get(sourceDatasetPK, 'dataType'))
}

function addValueWidget ({ addWidgetMenu, selected }) {
	let widgetProps = _.extend({}, DEFAULT_WIDGET_PROPERTIES, {
		section: 'datasets',
		domainObjectId: selected.dataset.id,
		startingDatasetId: selected.dataset.id,
		fieldId: selected.fieldId,
		visibleLinkedFieldId: selected.visibleLinkedFieldId,
		aggregationType: defaultDatasetValueWidgetAggregationType(selected.field),
		// Note: don't set the dataType here. The field's dataType will be used
		// by default, so if we leave it empty, the widget will use the new
		// dataType if the field's dataType changes
		fontSize: 37,
		[SHAPE]: 'rectangle',
		[FILL]: NONE,
		[SHAPE_TEXT_TYPE]: DATA_SOURCE,
		// Since the fill is NONE, this specifies the text color
		[SHAPE_SPECIFIC_COLOR]: 'gray-darker',
		background: WHITE,
		shapeHeight: WIDGET_HEIGHT - TITLE_HEIGHT - (2 * BORDER_WIDTH)
	})

	widgetProps.label = defaultDatasetSourceLabel(selected.dataset, widgetProps)

	// Make sure dataset is in dashboard-level data
	let dataset = selected.dataset.toJSON()
	addWidgetMenu.model.data.datasets(dataset.id, dataset)

	let widget = new ShapeModel(
		{
			properties: widgetProps
		}, {
			domainObject: dataset,
			section: 'datasets'
		})

	let wobAttributes = _.extend({},
		DEFAULT_WOB_ATTRIBUTES,
		{
			outlineWidth: null,
			// From the old dataset-value-widget-model defaults
			width: 200,
			height: WIDGET_HEIGHT
		})

	// no need to do a data fetch, because we already have the dataset and passed it in
	// to the widget model (via the domainObject option)
	createWob(addWidgetMenu.canvas, widget,
		{ fetchData: false, wobAttributes },
		addWidgetMenu)
}

/**
 * The desired total initial height of the widget
 */
const WIDGET_HEIGHT = 110

/**
 * The height of the title bar (seems to be some combination of the rendered
 * text size, margins, etc)
 */
const TITLE_HEIGHT = 37

/**
 * The widget's border width (around every side)
 */
const BORDER_WIDTH = 1

export default createDatasetValueShapeMenu

createDatasetValueShapeMenu.__filename__ = 'dashboards/widgets/shape/create-dataset-value-shape-menu.js'
